import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Loader2, Scale, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  useCarteraAging,
  useCarteraSummary,
} from "@/lib/api/cartera.queries";
import { useActiveCompany } from "@/lib/company-context";
import type { CarteraSide } from "@/lib/api/cartera";

export const Route = createFileRoute("/companies/$companyId/cartera/")({
  component: CarteraIndexPage,
});

function CarteraIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <CarteraIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function CarteraIndexContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [side, setSide] = useState<CarteraSide>("receivables");
  const [asOf, setAsOf] = useState("");

  const summaryQuery = useCarteraSummary(companyId, asOf || undefined);
  const agingQuery = useCarteraAging(companyId, side, asOf || undefined);

  const summary = summaryQuery.data;
  const aging = agingQuery.data;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("cartera.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("cartera.subtitle")}
          </p>
        </div>
        <div>
          <Label htmlFor="cartera-asof">{t("cartera.as_of")}</Label>
          <Input
            id="cartera-asof"
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            className="w-44"
          />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <SummaryCard
          title={
            side === "receivables"
              ? t("cartera.summary.total_cxc")
              : t("cartera.summary.total_cxp")
          }
          value={
            summary
              ? formatCop(
                  side === "receivables"
                    ? summary.receivables.total
                    : summary.payables.total,
                )
              : "…"
          }
          icon={<Scale className="h-5 w-5 text-primary" />}
        />
        <SummaryCard
          title={t("cartera.summary.overdue")}
          value={
            summary
              ? formatCop(
                  side === "receivables"
                    ? summary.receivables.overdue
                    : summary.payables.overdue,
                )
              : "…"
          }
          tone="warning"
          icon={<Scale className="h-5 w-5 text-amber-600" />}
        />
        <SummaryCard
          title={
            side === "receivables"
              ? t("cartera.summary.customers")
              : t("cartera.summary.suppliers")
          }
          value={
            summary
              ? String(
                  side === "receivables"
                    ? summary.receivables.customers
                    : summary.payables.suppliers,
                )
              : "…"
          }
          icon={<Users className="h-5 w-5 text-primary" />}
        />
      </div>

      <Tabs value={side} onValueChange={(v) => setSide(v as CarteraSide)}>
        <TabsList>
          <TabsTrigger value="receivables">{t("cartera.tab_cxc")}</TabsTrigger>
          <TabsTrigger value="payables">{t("cartera.tab_cxp")}</TabsTrigger>
        </TabsList>
      </Tabs>

      {agingQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : agingQuery.isError ? (
        <ErrorState
          title={t("cartera.error_title")}
          description={t("cartera.error_body")}
          error={agingQuery.error}
          onRetry={() => void agingQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : !aging || aging.rows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <h2 className="text-lg font-semibold">
              {t("cartera.empty_title")}
            </h2>
            <p className="max-w-sm text-sm text-muted-foreground">
              {side === "receivables"
                ? t("cartera.empty_body_cxc")
                : t("cartera.empty_body_cxp")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("cartera.aging_title", { asOf: aging.asOf })}
            </CardTitle>
            <CardDescription>
              {t("cartera.aging_subtitle", {
                invoices: aging.invoiceCount,
                terceros: aging.terceroCount,
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("cartera.col_tercero")}</TableHead>
                  <TableHead className="text-right">
                    {t("cartera.bucket.current")}
                  </TableHead>
                  <TableHead className="text-right">1–30</TableHead>
                  <TableHead className="text-right">31–60</TableHead>
                  <TableHead className="text-right">61–90</TableHead>
                  <TableHead className="text-right">90+</TableHead>
                  <TableHead className="text-right">
                    {t("cartera.col_total")}
                  </TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {aging.rows.map((row) => (
                  <TableRow key={row.terceroId}>
                    <TableCell>
                      <p className="max-w-[220px] truncate text-sm font-medium">
                        {row.terceroName}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {t("cartera.invoice_count", { count: row.invoiceCount })}
                      </p>
                    </TableCell>
                    <MoneyCell value={row.current} />
                    <MoneyCell value={row.d1_30} />
                    <MoneyCell value={row.d31_60} />
                    <MoneyCell value={row.d61_90} />
                    <MoneyCell value={row.d90_plus} emphasizeOverdue />
                    <TableCell className="text-right font-mono text-sm font-semibold">
                      {formatCop(row.total)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="ghost" size="sm">
                        <Link
                          to="/companies/$companyId/cartera/$terceroId"
                          params={{ companyId, terceroId: row.terceroId }}
                          search={{
                            side,
                            ...(asOf ? { to: asOf } : {}),
                          }}
                        >
                          {t("cartera.view_statement")}
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow className="bg-muted/40 font-semibold">
                  <TableCell>{t("cartera.totals_row")}</TableCell>
                  <MoneyCell value={aging.totals.current} />
                  <MoneyCell value={aging.totals.d1_30} />
                  <MoneyCell value={aging.totals.d31_60} />
                  <MoneyCell value={aging.totals.d61_90} />
                  <MoneyCell value={aging.totals.d90_plus} emphasizeOverdue />
                  <TableCell className="text-right font-mono text-sm font-bold">
                    {formatCop(aging.totals.total)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SummaryCard({
  title,
  value,
  icon,
  tone,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  tone?: "warning";
}) {
  return (
    <Card className={tone === "warning" ? "border-amber-200 dark:border-amber-900/40" : undefined}>
      <CardContent className="flex items-center gap-3 pt-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
          {icon}
        </div>
        <div className="min-w-0">
          <p className="truncate text-xs text-muted-foreground">{title}</p>
          <p className="truncate font-mono text-lg font-semibold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function MoneyCell({
  value,
  emphasizeOverdue,
}: {
  value: number;
  emphasizeOverdue?: boolean;
}) {
  const formatCop = useMoneyFormatter();
  return (
    <TableCell
      className={`text-right font-mono text-sm ${
        value === 0
          ? "text-muted-foreground/50"
          : emphasizeOverdue
            ? "text-destructive"
            : ""
      }`}
    >
      {value === 0 ? "—" : formatCop(value)}
    </TableCell>
  );
}
